import React from 'react'
import { getCommitChartStackColors } from './commitChartPalette'
import { FeeListItem, IssueInvestorCommitment, SummarizedFeeLevelRow } from '@finance-lab/utils/types'
import { getCommitChartColorIndex, getCommitChartSegmentLabel, randomBlue } from '../../pages/issues/utils'
import HorizontalBarChart from './utils/HorizontalBarChart'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { roundFloat } from '../../packages/utils/numberFormatters'

type SerieType = {
    label: string
    data: [number]
    color: string
}

export const CommitChartWithInvestors = ({
    isLeftAlign,
    investors,
    size,
    skipAnimation,
    summarizedFeeLevels,
    totalGuarantCommitmentInPercentage,
    totalSubscriptionCommitmentInPercentage,
}: {
    fees: Array<FeeListItem>
    isLeftAlign?: boolean
    investors: Array<Partial<Pick<IssueInvestorCommitment, 'feeLevel' | 'isSeller'>>>
    size: 'small' | 'large'
    skipAnimation?: boolean
    summarizedFeeLevels: Array<SummarizedFeeLevelRow>
    totalGuarantCommitmentInPercentage: number
    totalSubscriptionCommitmentInPercentage: number
}) => {
    const theme = useTheme()
    const { t } = useTranslation()

    const chartColors = getCommitChartStackColors(theme)
    const investorsExcludingSellers = investors.filter((investor) => !investor.isSeller)
    const maxFeeLevel = Math.max(0, ...investorsExcludingSellers.map((investor) => investor.feeLevel ?? 0))

    const totalCommitment = Math.min(
        summarizedFeeLevels.reduce(
            (acc, commitment) =>
                Number(commitment.amountInPercentage) > 0 ? acc + Number(commitment.amountInPercentage || 0) : acc,
            0,
        ),
        100,
    )

    let notGuaranteed = 100 - totalGuarantCommitmentInPercentage - totalSubscriptionCommitmentInPercentage

    const totalTaCommitmentPercentage = summarizedFeeLevels
        .filter((row) => row.feeLevel === 0)
        .reduce((sum, row) => sum + Number(row.amountInPercentage), 0)

    const seenMiddleDownForFeeLevel = new Set<number>()

    const series = summarizedFeeLevels.reduce((acc: SerieType[], item) => {
        const { feeLevel, amountInPercentage } = item
        const colorIdx = getCommitChartColorIndex(item)
        const color = chartColors[colorIdx] ?? randomBlue()

        if (item.middleDown) {
            if (!seenMiddleDownForFeeLevel.has(feeLevel)) {
                seenMiddleDownForFeeLevel.add(feeLevel)
                const notAssured = item.middleDown - totalTaCommitmentPercentage
                if (notAssured > 0) {
                    notGuaranteed -= notAssured
                    acc.push({
                        label: t('pages.issues.feeLevels.notGuaranteed'),
                        data: [roundFloat(notAssured)],
                        color: theme.palette.grey[200],
                    })
                }
            }
            acc.push({
                label: getCommitChartSegmentLabel(item, maxFeeLevel),
                data: [roundFloat(amountInPercentage)],
                color,
            })
            return acc
        }

        if (item.isTopDown && notGuaranteed > 0) {
            acc.push({
                label: t('pages.issues.feeLevels.notGuaranteed'),
                data: [roundFloat(notGuaranteed)],
                color: theme.palette.grey[200],
            })
            notGuaranteed = 0
        }

        acc.push({
            label: getCommitChartSegmentLabel(item, maxFeeLevel),
            data: [roundFloat(amountInPercentage)],
            color,
        })

        return acc
    }, [])

    if (notGuaranteed > 0) {
        series.push({
            label: t('pages.issues.feeLevels.notGuaranteed'),
            data: [roundFloat(notGuaranteed)],
            color: theme.palette.grey[200],
        })
        notGuaranteed = 0
    }

    return (
        <HorizontalBarChart
            isLeftAlign={isLeftAlign}
            labelValue={totalCommitment || 0}
            series={series}
            size={size}
            skipAnimation={skipAnimation}
        />
    )
}
